import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import ClientToolbar from "../components/clients/ClientToolbar";
import { getClientMasterRole, hasFullClientAccess, hasLimitedClientEditAccess } from "../utils/clientPermissions";
import ClientTable from "../components/clients/ClientTable";
import ClientFormModal from "../components/clients/ClientFormModal";
import ViewClientModal from "../components/clients/ViewClientModal";
import {
  clientHasLinkedTasks,
  createClient,
  deleteClient,
  findDuplicatePan,
  subscribeToClients,
  subscribeToStaff,
  subscribeToTasks,
  updateClient,
  updateClientLimited,
  updateClientNonDestructive,
} from "../services/clientService";
import {
  EMPTY_CLIENT,
  normalizeClient,
  validateClient,
} from "../utils/clientValidation";
import {
  createClientPortalAccount,
  sendClientPortalResetEmail,
  setClientPortalAccountActive,
} from "../services/authAdminService";
import {
  exportClientImportErrors,
  exportClientsToExcel,
  readClientsFromExcel,
} from "../utils/excelClientHelper";

export default function Clients() {
  const { userData } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const role = getClientMasterRole(userData);
  const fullClientAccess = hasFullClientAccess(userData);
  const limitedClientEdit = hasLimitedClientEditAccess(userData);

  const [clients, setClients] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [staffMap, setStaffMap] = useState({});
  const [searchTerm, setSearchTerm] = useState("");

  const [formMode, setFormMode] = useState(null);
  const [formClient, setFormClient] = useState(EMPTY_CLIENT);
  const [selectedClient, setSelectedClient] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);

  useEffect(() => {
    const onError = (error) => console.error("Client module listener error:", error);

    const unsubscribeClients = subscribeToClients(setClients, onError);
    const unsubscribeTasks = subscribeToTasks(setTasks, onError);
    const unsubscribeStaff = subscribeToStaff(setStaffMap, onError);

    return () => {
      unsubscribeClients();
      unsubscribeTasks();
      unsubscribeStaff();
    };
  }, []);

  useEffect(() => {
    if (!selectedClient?.id) return;
    const latest = clients.find((item) => item.id === selectedClient.id);
    if (latest) setSelectedClient(latest);
  }, [clients, selectedClient?.id]);

  useEffect(() => {
    const clientId = searchParams.get("clientId");
    if (!clientId || !clients.length) return;
    const matchedClient = clients.find((item) => String(item.id) === String(clientId));
    if (!matchedClient) return;
    setSelectedClient(matchedClient);
    setSearchTerm(matchedClient.name || matchedClient.clientName || "");
    const next = new URLSearchParams(searchParams);
    next.delete("clientId");
    setSearchParams(next, { replace: true });
  }, [clients, searchParams, setSearchParams]);

  const filteredClients = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    if (!term) return clients;

    return clients.filter((client) =>
      [
        client.name,
        client.pan,
        client.gstin,
        client.contactPerson,
        client.contact,
        client.mobile,
        client.email,
        client.remarks,
        client.notes,
        ...(client.additionalContacts || []).flatMap((item) => [item.name, item.mobile]),
        client.portalLoginEmail,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    );
  }, [clients, searchTerm]);

  const selectedClientTasks = useMemo(
    () => tasks.filter((task) => task.clientId === selectedClient?.id),
    [tasks, selectedClient]
  );

  if (userData && !["admin", "partner", "accounts"].includes(role)) {
    return <Navigate to="/" replace />;
  }

  const openAddModal = () => {
    if (!fullClientAccess) return;
    setErrors({});
    setFormClient({ ...EMPTY_CLIENT });
    setFormMode("add");
  };

  const openEditModal = (client) => {
    setErrors({});
    setFormClient(normalizeClient(client));
    setFormMode("edit");
  };

  const closeFormModal = () => {
    if (isSaving) return;
    setFormMode(null);
    setFormClient(EMPTY_CLIENT);
    setErrors({});
  };

  const handleSave = async () => {
    if (!fullClientAccess && !(limitedClientEdit && formMode === "edit")) return;
    const allValidationErrors = validateClient(formClient);
    const validationErrors = limitedClientEdit
      ? Object.fromEntries(Object.entries(allValidationErrors).filter(([key]) =>
          ["pan", "gstin", "mobile", "email", "pinCode"].includes(key) || key.startsWith("additionalContact_")
        ))
      : allValidationErrors;
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) return;

    const duplicate = findDuplicatePan(
      clients,
      formClient.pan,
      formMode === "edit" ? formClient.id : null
    );

    if (
      duplicate &&
      !window.confirm(
        `PAN ${formClient.pan.toUpperCase()} already exists for "${duplicate.name}". Continue anyway?`
      )
    ) {
      return;
    }

    try {
      setIsSaving(true);

      if (limitedClientEdit) await updateClientLimited(formClient.id, formClient);
      else if (formMode === "add") await createClient(formClient);
      else await updateClient(formClient.id, formClient);

      closeFormModal();
    } catch (error) {
      console.error("Error saving client:", error);
      alert(`Failed to save client: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (client) => {
    if (!fullClientAccess) return;
    if (clientHasLinkedTasks(tasks, client.id)) {
      alert(
        "This client has linked tasks and cannot be deleted. Mark the client as Inactive instead."
      );
      return;
    }

    if (
      !window.confirm(
        `Delete "${client.name}"? This action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      await deleteClient(client.id);
    } catch (error) {
      console.error("Error deleting client:", error);
      alert(`Failed to delete client: ${error.message}`);
    }
  };

  const handleImport = async (file) => {
    if (!fullClientAccess) return;
    try {
      const importedClients = await readClientsFromExcel(file);
      const existingClientIds = new Set(clients.map((client) => client.id));

      const rowsToCreate = [];
      const rowsToUpdate = [];
      const invalidRows = [];
      const duplicatePanWarnings = [];

      importedClients.forEach((client) => {
        if (client.id) {
          if (!existingClientIds.has(client.id)) {
            invalidRows.push({
              row: client.importRowNumber,
              name: client.name || "(blank)",
              pan: client.pan || "",
              gstin: client.gstin || "",
              reason: "Client ID does not match any existing client.",
            });
            return;
          }

          const existing = clients.find((item) => item.id === client.id);
          const supplied = new Set(client.importPresentFields || []);
          const mergedForValidation = normalizeClient({
            ...existing,
            ...Object.fromEntries(
              Object.entries(client).filter(([key]) => supplied.has(key))
            ),
          });
          const rowErrors = validateClient(mergedForValidation);
          if (Object.keys(rowErrors).length > 0) {
            invalidRows.push({
              row: client.importRowNumber,
              name: mergedForValidation.name || "(blank)",
              pan: mergedForValidation.pan || "",
              gstin: mergedForValidation.gstin || "",
              reason: Object.values(rowErrors).join(" "),
            });
            return;
          }

          if (supplied.has("pan")) {
            const duplicate = findDuplicatePan(clients, mergedForValidation.pan, client.id);
            if (duplicate) {
              duplicatePanWarnings.push(
                `Row ${client.importRowNumber}: PAN ${mergedForValidation.pan} also belongs to "${duplicate.name}".`
              );
            }
          }
          rowsToUpdate.push(client);
          return;
        }

        const rowErrors = validateClient(client);
        if (Object.keys(rowErrors).length > 0) {
          invalidRows.push({
            row: client.importRowNumber,
            name: client.name || "(blank)",
            pan: client.pan || "",
            gstin: client.gstin || "",
            reason: Object.values(rowErrors).join(" "),
          });
          return;
        }

        const duplicate = findDuplicatePan(clients, client.pan);
        if (duplicate) {
          duplicatePanWarnings.push(
            `Row ${client.importRowNumber}: PAN ${client.pan} already exists for "${duplicate.name}".`
          );
        }
        rowsToCreate.push(client);
      });

      if (rowsToCreate.length === 0 && rowsToUpdate.length === 0) {
        if (invalidRows.length > 0) {
          exportClientImportErrors(invalidRows);
        }

        alert(
          `No valid rows are available for import.\n\n` +
            `${invalidRows.length} invalid row(s) found.\n` +
            `An Excel error report has been downloaded.`
        );
        return;
      }

      const invalidPreview = invalidRows
        .slice(0, 12)
        .map(
          (item) =>
            `Row ${item.row} — ${item.name} — ${item.reason}`
        )
        .join("\n");

      const duplicatePreview = duplicatePanWarnings
        .slice(0, 8)
        .join("\n");

      const summary =
        `Import summary:\n` +
        `Existing clients to update: ${rowsToUpdate.length}\n` +
        `New clients to create: ${rowsToCreate.length}\n` +
        `Invalid rows to skip: ${invalidRows.length}\n` +
        `Duplicate PAN warnings: ${duplicatePanWarnings.length}`;

      const detailText =
        `${invalidRows.length ? `\n\nInvalid row details:\n${invalidPreview}` : ""}` +
        `${invalidRows.length > 12 ? `\n...and ${invalidRows.length - 12} more invalid row(s).` : ""}` +
        `${duplicatePanWarnings.length ? `\n\nDuplicate PAN details:\n${duplicatePreview}` : ""}` +
        `${duplicatePanWarnings.length > 8 ? `\n...and ${duplicatePanWarnings.length - 8} more warning(s).` : ""}`;

      const confirmed = window.confirm(
        `${summary}${detailText}\n\n` +
          `Press OK to continue with valid rows.\n` +
          `Press Cancel to stop the import.`
      );

      if (!confirmed) {
        if (
          invalidRows.length > 0 &&
          window.confirm(
            "Would you like to download an Excel report of all invalid rows?"
          )
        ) {
          exportClientImportErrors(invalidRows);
        }
        return;
      }

      for (const client of rowsToUpdate) {
        await updateClientNonDestructive(client.id, client, client.importPresentFields || []);
      }

      for (const client of rowsToCreate) {
        await createClient(client);
      }

      if (invalidRows.length > 0) {
        exportClientImportErrors(invalidRows);
      }

      alert(
        `Import completed successfully.\n` +
          `Updated: ${rowsToUpdate.length}\n` +
          `Created: ${rowsToCreate.length}\n` +
          `Skipped: ${invalidRows.length}` +
          `${invalidRows.length ? "\n\nAn Excel error report has been downloaded." : ""}`
      );
    } catch (error) {
      console.error("Excel import failed:", error);
      alert(`Excel import failed: ${error.message}`);
    }
  };

  const handleSendInviteFromForm = async () => {
    if (!fullClientAccess) return;
    if (formMode !== "edit" || !formClient?.id) return;

    const validationErrors = validateClient(formClient);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;
    if (!formClient.portalEnabled) return alert("Enable Client Portal first.");
    if (!formClient.portalLoginEmail) return alert("Enter a portal login email first.");
    if (formClient.portalAuthUid) return alert("A portal login is already linked to this client.");

    const duplicate = findDuplicatePan(clients, formClient.pan, formClient.id);
    if (duplicate && !window.confirm(`PAN ${formClient.pan.toUpperCase()} already exists for "${duplicate.name}". Continue anyway?`)) return;

    if (!window.confirm(`Send a secure client portal invite to ${formClient.portalLoginEmail}?`)) return;

    setPortalBusy(true);
    try {
      // Save the latest portal-enabled/email values before the secure function validates them.
      await updateClient(formClient.id, { ...formClient, portalAccessStatus: "not_invited" });
      const result = await createClientPortalAccount({
        clientId: formClient.id,
        email: formClient.portalLoginEmail,
        name: formClient.contactPerson || formClient.name,
      });
      setFormClient((current) => ({
        ...current,
        portalAuthUid: result.uid || current.portalAuthUid,
        portalAccessStatus: "invited",
      }));
      alert("Client portal invite sent successfully. The client can use the email link to set their password.");
    } catch (error) {
      console.error("Client portal invite failed:", error);
      alert(`Unable to send client invite: ${error.message}`);
    } finally {
      setPortalBusy(false);
    }
  };

  const handleResetPortalFromForm = async () => {
    if (!fullClientAccess) return;
    if (!formClient?.portalLoginEmail) return;
    if (!window.confirm(`Send a password reset email to ${formClient.portalLoginEmail}?`)) return;
    setPortalBusy(true);
    try {
      await sendClientPortalResetEmail(formClient.portalLoginEmail);
      alert("Password reset email sent.");
    } catch (error) {
      alert(`Unable to send password reset: ${error.message}`);
    } finally {
      setPortalBusy(false);
    }
  };

  const handleTogglePortalFromForm = async (makeActive) => {
    if (!fullClientAccess) return;
    if (!formClient?.id) return;
    if (!window.confirm(`${makeActive ? "Enable" : "Disable"} client login for ${formClient.portalLoginEmail}?`)) return;
    setPortalBusy(true);
    try {
      await setClientPortalAccountActive(formClient.id, makeActive);
      setFormClient((current) => ({ ...current, portalAccessStatus: makeActive ? "active" : "disabled" }));
    } catch (error) {
      alert(`Unable to ${makeActive ? "enable" : "disable"} client login: ${error.message}`);
    } finally {
      setPortalBusy(false);
    }
  };

  const handleCreatePortal = async () => {
    if (!fullClientAccess) return;
    if (!selectedClient) return;
    if (!selectedClient.portalEnabled) return alert("Enable Client Portal in Edit Client first.");
    if (!selectedClient.portalLoginEmail) return alert("Enter a portal login email in Client Master first.");
    if (!window.confirm(`Create a client login for ${selectedClient.portalLoginEmail}? A password setup email will be sent.`)) return;
    setPortalBusy(true);
    try {
      await createClientPortalAccount({ clientId: selectedClient.id, email: selectedClient.portalLoginEmail, name: selectedClient.contactPerson || selectedClient.name });
      alert("Client login created. Password setup email sent successfully.");
    } catch (error) {
      console.error("Client portal creation failed:", error);
      alert(`Unable to create client login: ${error.message}`);
    } finally { setPortalBusy(false); }
  };

  const handleResetPortal = async () => {
    if (!fullClientAccess) return;
    if (!selectedClient?.portalLoginEmail) return;
    if (!window.confirm(`Send a password reset email to ${selectedClient.portalLoginEmail}?`)) return;
    setPortalBusy(true);
    try { await sendClientPortalResetEmail(selectedClient.portalLoginEmail); alert("Password reset email sent."); }
    catch (error) { alert(`Unable to send password reset: ${error.message}`); }
    finally { setPortalBusy(false); }
  };

  const handleTogglePortal = async (makeActive) => {
    if (!fullClientAccess) return;
    if (!selectedClient) return;
    if (!window.confirm(`${makeActive ? "Enable" : "Disable"} client login for ${selectedClient.portalLoginEmail}?`)) return;
    setPortalBusy(true);
    try { await setClientPortalAccountActive(selectedClient.id, makeActive); }
    catch (error) { alert(`Unable to ${makeActive ? "enable" : "disable"} client login: ${error.message}`); }
    finally { setPortalBusy(false); }
  };

  return (
    <div className="p-4 md:p-6">
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-gray-900">Client Master</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage client profiles and review their linked work.
        </p>
      </div>

      <ClientToolbar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onAdd={fullClientAccess ? openAddModal : undefined}
        onImport={fullClientAccess ? handleImport : undefined}
        onExport={() => exportClientsToExcel(clients)}
      />

      <ClientTable
        clients={filteredClients}
        onEdit={openEditModal}
        onView={setSelectedClient}
        onDelete={fullClientAccess ? handleDelete : undefined}
      />

      {formMode && (
        <ClientFormModal
          title={formMode === "add" ? "Add Client" : limitedClientEdit ? "Edit Client Contact Details" : "Edit Client"}
          limitedMode={limitedClientEdit}
          client={formClient}
          errors={errors}
          isSaving={isSaving}
          portalBusy={portalBusy}
          onChange={setFormClient}
          onSave={handleSave}
          onSendInvite={handleSendInviteFromForm}
          onResetPortal={handleResetPortalFromForm}
          onTogglePortal={handleTogglePortalFromForm}
          onClose={closeFormModal}
        />
      )}

      {selectedClient && (
        <ViewClientModal
          client={selectedClient}
          tasks={selectedClientTasks}
          staffMap={staffMap}
          portalBusy={portalBusy}
          onCreatePortal={fullClientAccess ? handleCreatePortal : undefined}
          onResetPortal={fullClientAccess ? handleResetPortal : undefined}
          onTogglePortal={fullClientAccess ? handleTogglePortal : undefined}
          onClose={() => setSelectedClient(null)}
        />
      )}
    </div>
  );
}
